import Text from 'components/Text/Text';
import React, { useContext, useEffect } from 'react';
import { IEditCategoryContext, EditCategoryContext } from '../../../../editCategoryState/editCategoryContext';
import { CustomerSelect } from './components/CustomerSelect';
import { RestaurantsMultipleSelect } from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import { NameCategoryInput } from './components/NameCategoryInput';
import { CategoryTypeSelect } from './components/CategoryTypeSelect';
import { IsRestrictedCheckBox } from './components/IsRestrictedCheckBox';
import { HasSchedulePriorityCheckBox } from './components/HasSchedulePriorityCheckBox';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { BtnBlock } from './components/BtnBlock';
import { Form } from 'antd';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setIDCustomer,
		setFetchedCustomers,
		setIDsRestaurant,
		setFetchedRestaurants,
		changeLoaderStatus,
		changeNameOfCategory,
		toggleIsRestricted,
		toggleHasSchedulePriority,
		setDefaultCustomerLanguage,
		setTranslationTab,
		changeNameTranslation,
		changeActivationSchedule,
		changeDeactivationSchedule,
		setCategoryType,
		isLoading,
		fileId,
		filePath,
		categoryID,
		IDSelectedCustomer,
		initialIDSelectedCustomer,
		IDsSelectedRestaurant,
		initialIDsSelectedRestaurant,
		nameOfCategory,
		categoryType,
		isRestricted,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		translations,
		defaultCustomerLanguage,
		selectedTranslationTab,
	} = useContext<IEditCategoryContext>(EditCategoryContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			IDSelectedCustomer,
			IDsSelectedRestaurant,
			nameOfCategory,
			categoryType,
			isRestricted,
			hasSchedulePriority,
			activationSchedule,
			deactivationSchedule,
		});
	}, [
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		nameOfCategory,
		categoryType,
		isRestricted,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		setDataForSubmit({
			id: categoryID,
			name: nameOfCategory,
			type: categoryType || 'Default',
			fileId: getFileIdForSubmit({ fileId, filePath }),
			isRestricted,
			restaurantIds: IDsSelectedRestaurant,
			overrideScheduleAvailability: hasSchedulePriority,
			activationCron: activationSchedule || null,
			deactivationCron: deactivationSchedule || null,
			translations,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<div className={style.infoWrapper}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#circleInfoTransparent`} />
						</svg>
						<Text
							translationNameSpace="form"
							translationKey="warningBeforeCategoryChange"
							theme="secondary"
							size="s"
							display="inline"
						/>
					</div>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<NameCategoryInput changeNameOfCategory={changeNameOfCategory} />
					<CategoryTypeSelect setCategoryType={setCategoryType} />
					<CustomerSelect
						setIDCustomer={setIDCustomer}
						setFetchedCustomers={setFetchedCustomers}
						initialIDSelectedCustomer={initialIDSelectedCustomer}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restrictionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsRestrictedCheckBox toggleIsRestricted={toggleIsRestricted} isRestricted={isRestricted} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="translationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<MenuTranslationForm
						className={style.translationForm}
						setDefaultCustomerLanguage={setDefaultCustomerLanguage}
						setTranslationTab={setTranslationTab}
						changeNameTranslation={changeNameTranslation}
						isShowLanguageName={false}
						IDSelectedCustomer={IDSelectedCustomer}
						translations={translations}
						selectedTranslationTab={selectedTranslationTab}
						nameLabel="nameCategoryLabel"
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<HasSchedulePriorityCheckBox
						toggleHasSchedulePriority={toggleHasSchedulePriority}
						hasSchedulePriority={hasSchedulePriority}
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantListHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<RestaurantsMultipleSelect
						setIDsRestaurant={setIDsRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
						changeLoaderStatus={changeLoaderStatus}
						IDSelectedCustomer={IDSelectedCustomer}
						initialIDsSelectedRestaurant={initialIDsSelectedRestaurant}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				{!isLoading && <BtnBlock changeTypeOfModalWindow={changeTypeOfModalWindow} />}
			</Form>
		</div>
	);
};
